import { useState } from 'react'
import { HelpCircle, Save, MessageSquare, Upload, Globe, FileText, X, CheckCircle2 } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { generateId, getStatusColor } from '../utils/helpers'

const qaTypes = ['Rufus QA', 'Cosmo场景QA', 'Alexa语音搜索QA', 'Return Prevention QA', '美国真人买家语气QA', '竞品反推QA', '广告关键词迁移QA']

const outputFields = ['ASIN','站点','QA类型','场景','人群','问题','回答','覆盖关键词','对应卖点','退货预防点','Alexa语音问法','Rufus意图','Cosmo场景','是否可发布','风险提示']

export default function QAWorkbench() {
  const { agents, prompts, knowledgeBases, tasks, setTasks, qaBatchConfigs, canExecute } = useApp()
  const qaAgents = agents.filter(a => a.module === 'QA' && (a.status === '主用' || a.status === '测试'))
  const [agentId, setAgentId] = useState('')
  const [inputMode, setInputMode] = useState('url')
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)

  // URL mode
  const [asin, setAsin] = useState('')
  const [amazonUrl, setAmazonUrl] = useState('')
  const [site, setSite] = useState('US')
  const [scrapeOptions, setScrapeOptions] = useState({ title: true, bullets: true, desc: true, aplus: false, qa: false, reviews: false })

  // Paste mode
  const [title, setTitle] = useState('')
  const [bullets, setBullets] = useState('')
  const [description, setDescription] = useState('')
  const [searchTerms, setSearchTerms] = useState('')
  const [params, setParams] = useState('')

  // Upload mode
  const [uploadedFiles, setUploadedFiles] = useState([])

  // QA config
  const [selectedQaTypes, setSelectedQaTypes] = useState(['Rufus QA'])
  const [qaCount, setQaCount] = useState(10)
  const [tone, setTone] = useState(true)
  const [alexa, setAlexa] = useState(false)
  const [returnPrev, setReturnPrev] = useState(true)
  const [dedup, setDedup] = useState(true)

  const agent = agents.find(a => a.id === agentId)

  const toggleQaType = (t) => setSelectedQaTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files)
    setUploadedFiles([...uploadedFiles, ...files.map(f => ({ id: generateId(), name: f.name }))])
  }

  const execute = () => {
    if (!canExecute()) return alert('无权限')
    if (!agentId) return alert('请选择Agent')
    setLoading(true)
    setTimeout(() => {
      const inputSummary = inputMode === 'url' ? `ASIN: ${asin} / URL: ${amazonUrl}`
        : inputMode === 'paste' ? `标题: ${title.substring(0,30)}...` : `上传文件: ${uploadedFiles.length}个`
      setOutput(`【QA 批量生成结果】\n\n输入方式: ${inputMode === 'url' ? 'Amazon链接' : inputMode === 'paste' ? '粘贴文案' : '上传资料'}\n${inputSummary}\n\n生成配置:\n- QA类型: ${selectedQaTypes.join(', ')}\n- 数量: ${qaCount}\n- 美国真人语气: ${tone ? '是' : '否'}\n- Alexa增强: ${alexa ? '是' : '否'}\n- 退货预防: ${returnPrev ? '是' : '否'}\n- 去重: ${dedup ? '是' : '否'}\n\n=== 示例输出 ===\nQ1: What makes this product different from others?\nA1: Our product features advanced technology with premium materials...\n[场景: 购买决策] [人群: 科技爱好者] [覆盖关键词: premium, advanced] [卖点: 材质] [退货预防: 已说明兼容性]\n[Alexa: Hey Alexa, what's the best premium product for home use?]\n[Rufus意图: 对比购买] [Cosmo场景: 家庭办公]\n[是否可发布: 是] [风险提示: 无]\n\nQ2: Is this easy to set up?\nA2: Yes, it comes with a detailed user manual and requires no tools...\n[场景: 安装咨询] [人群: 新手用户] ...\n\n共生成 ${qaCount} 条 QA，已按类型分类并去重。`)
      setLoading(false)
    }, 1500)
  }

  const save = () => {
    const newTask = { id: generateId(), name: `${asin || title.substring(0,15)} QA生成`, type: 'QA',
      agentId, promptVersion: prompts.find(p => p.id === agent?.promptId)?.version || '-',
      knowledgeVersion: knowledgeBases.filter(k => agent?.knowledgeIds?.includes(k.id)).map(k => k.version).join(', ') || '-',
      model: agent?.defaultModel || '-', inputFiles: [], inputText: output.split('=== 示例输出 ===')[0],
      aiOutput: output, humanEdit: null, adopted: false, reviewStatus: '待审核',
      owner: 'Leo', createdAt: new Date().toISOString() }
    setTasks([newTask, ...tasks]); alert('已保存')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <HelpCircle size={24} className="text-cyan-600" />
        <h2 className="text-xl font-bold text-slate-800">QA / Rufus / Cosmo / Alexa 台</h2>
      </div>

      <div className="flex gap-2">
        {['url','paste','upload'].map(mode => (
          <button key={mode} onClick={() => setInputMode(mode)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${inputMode === mode ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
            {mode === 'url' ? 'Amazon链接分析' : mode === 'paste' ? '粘贴文案' : '上传资料'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">选择 QA Agent</label>
            <select className="select" value={agentId} onChange={e => setAgentId(e.target.value)}>
              <option value="">请选择...</option>
              {qaAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {inputMode === 'url' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Amazon URL</label><input className="input" value={amazonUrl} onChange={e => setAmazonUrl(e.target.value)} placeholder="https://www.amazon.com/dp/..." /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">ASIN</label><input className="input" value={asin} onChange={e => setAsin(e.target.value)} placeholder="B08XXXXXX" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">站点</label>
                <select className="select" value={site} onChange={e => setSite(e.target.value)}>
                  <option value="US">美国 (US)</option><option value="CA">加拿大 (CA)</option><option value="UK">英国 (UK)</option><option value="DE">德国 (DE)</option><option value="JP">日本 (JP)</option>
                </select>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500 mb-2">抓取选项（第一版模拟，后续接后端抓取）</div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(scrapeOptions).map(([k,v]) => (
                    <label key={k} className="flex items-center gap-1 text-sm text-slate-700">
                      <input type="checkbox" checked={v} onChange={e => setScrapeOptions({...scrapeOptions, [k]: e.target.checked})} />
                      {k === 'title' ? '标题' : k === 'bullets' ? '五点' : k === 'desc' ? '描述' : k === 'aplus' ? 'A+' : k === 'qa' ? '前台QA' : '评论'}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {inputMode === 'paste' && (
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">标题</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="产品标题..." /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">五点描述</label><textarea className="textarea h-24" value={bullets} onChange={e => setBullets(e.target.value)} placeholder="每行一个卖点..." /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">描述</label><textarea className="textarea h-16" value={description} onChange={e => setDescription(e.target.value)} placeholder="产品描述..." /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Search Terms</label><input className="input" value={searchTerms} onChange={e => setSearchTerms(e.target.value)} placeholder="关键词,用逗号分隔" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">产品参数</label><textarea className="textarea h-16" value={params} onChange={e => setParams(e.target.value)} placeholder="尺寸、重量、材质..." /></div>
            </div>
          )}

          {inputMode === 'upload' && (
            <div className="space-y-3">
              <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
                <Upload size={24} className="mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-600">上传 Listing文案 / 竞品QA / 评论表 / 竞品截图 / 参数表 / 退货原因表</p>
                <input type="file" multiple className="hidden" id="qa-upload" onChange={handleFileUpload} />
                <label htmlFor="qa-upload" className="mt-2 inline-block text-xs text-cyan-600 hover:underline cursor-pointer">点击选择文件</label>
              </div>
              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {uploadedFiles.map(f => (
                    <span key={f.id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">{f.name} <button onClick={() => setUploadedFiles(uploadedFiles.filter(x=>x.id!==f.id))}><X size={10}/></button></span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">QA 生成类型</label>
            <div className="flex flex-wrap gap-2">
              {qaTypes.map(t => (
                <button key={t} onClick={() => toggleQaType(t)} className={`px-2 py-1 rounded-lg text-xs ${selectedQaTypes.includes(t) ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">生成数量</label><input type="number" className="input" value={qaCount} onChange={e => setQaCount(Number(e.target.value))} /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">每类占比</label><input className="input" placeholder="自动平均分配" disabled /></div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-1 text-slate-700"><input type="checkbox" checked={tone} onChange={e => setTone(e.target.checked)} /> 美国真人语气</label>
            <label className="flex items-center gap-1 text-slate-700"><input type="checkbox" checked={alexa} onChange={e => setAlexa(e.target.checked)} /> 加强Alexa</label>
            <label className="flex items-center gap-1 text-slate-700"><input type="checkbox" checked={returnPrev} onChange={e => setReturnPrev(e.target.checked)} /> 退货预防</label>
            <label className="flex items-center gap-1 text-slate-700"><input type="checkbox" checked={dedup} onChange={e => setDedup(e.target.checked)} /> 去重</label>
          </div>

          <button onClick={execute} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            <MessageSquare size={16} /> {loading ? '生成中...' : `生成 ${qaCount} 条 QA`}
          </button>
        </div>

        <div className="space-y-4">
          {agent && (
            <div className="card p-4 bg-cyan-50 border-cyan-200">
              <div className="text-sm text-cyan-800 font-medium mb-1">当前Agent: {agent.name}</div>
              <div className="text-xs text-cyan-600">Prompt: {prompts.find(p => p.id === agent.promptId)?.name || '无'}</div>
            </div>
          )}

          {output && (
            <div className="card p-5">
              <h3 className="font-semibold text-slate-800 mb-3">生成结果</h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap font-mono min-h-[200px]">{output}</div>
              <div className="flex gap-3 mt-4">
                <button onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> 保存结果</button>
                <button className="btn-secondary text-sm">导出 CSV</button>
                <button className="btn-secondary text-sm">导出 Excel</button>
              </div>
            </div>
          )}

          {qaBatchConfigs.length > 0 && (
            <div className="card p-4">
              <h3 className="font-semibold text-slate-800 mb-3 text-sm">批量 QA 配置</h3>
              <div className="space-y-2">
                {qaBatchConfigs.map(cfg => (
                  <div key={cfg.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                    <div><div className="font-medium">{cfg.asin}</div><div className="text-xs text-slate-500">{cfg.site} · {cfg.qaType}</div></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
